import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useData } from '../contexts/DataContext';
import { useInterventions } from '../hooks/useInterventions';
import FilterPanel from '../components/Interventions/FilterPanel';
import InterventionTable from '../components/Interventions/InterventionTable';
import InterventionModal from '../components/Interventions/InterventionModal';
import TechnicentreValidationModal from '../components/Interventions/TechnicentreValidationModal';
import TechnicentreFinalValidationModal from '../components/Interventions/TechnicentreFinalValidationModal';
import EmailModal from '../components/Email/EmailModal';
import EmailPreviewModal from '../components/Email/EmailPreviewModal';
import GOFPanel from '../components/GOF/GOFPanel';
import HistoriqueGlobalModal from '../components/Interventions/HistoriqueGlobalModal';
import LineManagementModal from '../components/LineManagement/LineManagementModal';
import StatsModal from '../components/Stats/StatsModal';
import Sidebar from '../components/Layout/Sidebar';
import {
  Intervention,
  EmailData,
  EmailResponse,
} from '../types';
import { emailApi } from '../services/api';

interface DashboardProps {
  onShowLoading: (show: boolean) => void;
}

// ===== Composant Dashboard =====
const Dashboard: React.FC<DashboardProps> = ({
  onShowLoading,
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { campaigns, sites, technicentres, prestations } = useData();
  const {
    interventions,
    allInterventions,
    stats,
    filters,
    setFilters,
    loadInterventions,
    createIntervention,
    updateIntervention,
    deleteIntervention,
  } = useInterventions();

  const [showValidationLogs, setShowValidationLogs] = useState(false);
  const [showGOF, setShowGOF] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [showLineManagement, setShowLineManagement] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showFinalValidationModal, setShowFinalValidationModal] = useState(false);
  const [editingIntervention, setEditingIntervention] = useState<Intervention | undefined>();
  const [selectedEngines, setSelectedEngines] = useState<{ name: string; index: number }[]>([]);
  const [rejectedEngines, setRejectedEngines] = useState<{ engine: string; index: number; reason: string }[]>([]);
  const [pendingAction, setPendingAction] = useState<{ type: 'modify' | 'delete', intervention?: Intervention } | null>(null);
  const [emailData, setEmailData] = useState<EmailResponse | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [pendingCount, setPendingCount] = useState(0);
  const [historyNotifications, setHistoryNotifications] = useState(0);

  const fetchPendingCount = useCallback(async () => {
    if (user?.isGOF) {
      try {
        const response = await fetch('/api/pending-modifications/count', {
          credentials: 'include'
        });
        const data = await response.json();
        setPendingCount(data.count || 0);
      } catch (error) {
        console.error('Error fetching pending count:', error);
      }
    }
  }, [user?.isGOF]);

  const fetchHistoryNotifications = useCallback(async () => {
    if (!user?.isGOF && user) {
      try {
        const lastSeen = localStorage.getItem(`history_last_seen_${user.id}`) || '0';
        const response = await fetch('/api/interventions/historique/all', {
          credentials: 'include'
        });
        const data = await response.json();

        const userHistory = data.filter((entry: any) =>
          ['REQUEST_MODIFICATION', 'MODIFICATION_APPROVED', 'MODIFICATION_REJECTED'].includes(entry.type_action)
        );

        const unseenCount = userHistory.filter((entry: any) =>
          new Date(entry.modifie_le).getTime() > parseInt(lastSeen)
        ).length;

        setHistoryNotifications(unseenCount);
      } catch (error) {
        console.error('Error fetching history notifications:', error);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user?.isGOF) {
      void fetchPendingCount();
      const interval = setInterval(() => void fetchPendingCount(), 5000);

      const handleUpdate = () => {
        void fetchPendingCount();
      };

      window.addEventListener('pendingModificationsUpdated', handleUpdate);

      return () => {
        clearInterval(interval);
        window.removeEventListener('pendingModificationsUpdated', handleUpdate);
      };
    } else if (user) {
      void fetchHistoryNotifications();
      const interval = setInterval(() => void fetchHistoryNotifications(), 10000);

      return () => clearInterval(interval);
    }
  }, [user?.isGOF, user, fetchPendingCount, fetchHistoryNotifications]);

  useEffect(() => {
    const interval = setInterval(() => {
      void loadInterventions(activeFilter || undefined);
    }, 5000);

    const handleInterventionsUpdate = () => {
      void loadInterventions(activeFilter || undefined);
    };

    window.addEventListener('interventionsUpdated', handleInterventionsUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('interventionsUpdated', handleInterventionsUpdate);
    };
  }, [loadInterventions, activeFilter]);

  const handleFilterByStatus = useCallback((status: string) => {
    setActiveFilter(status);
    void loadInterventions(status);
  }, [loadInterventions]);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setActiveFilter('');
    void loadInterventions();
  }, [setFilters, loadInterventions]);

  const handleEditIntervention = useCallback((id: number) => {
    const intervention = interventions.find(i => i.id === id);
    setEditingIntervention(intervention);

    if (user?.role === 'technicentre') {
      setShowModifyModal(true);
    } else {
      setShowAddModal(true);
    }
  }, [interventions, user?.role]);

  const handleDeleteIntervention = useCallback(async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette intervention ?')) {
      return;
    }

    try {
      await deleteIntervention(id);
      showNotification('Intervention supprimée', 'success');

      await loadInterventions(activeFilter || undefined);
    } catch (error: any) {
      showNotification(error.message || 'Erreur lors de la suppression', 'error');
    }
  }, [deleteIntervention, showNotification, loadInterventions, activeFilter]);

  const handleDuplicateIntervention = useCallback((intervention: Intervention) => {

    const duplicatedIntervention: Partial<Intervention> = {
      campagne_nom: intervention.campagne_nom,
      site_nom: intervention.site_nom,
      technicentre_nom: intervention.technicentre_nom,
      prestation_nom: intervention.prestation_nom,
      contact: intervention.contact,
      numero_elan: intervention.numero_elan,
      lieu_dit: intervention.lieu_dit,
      disponibilite_a_partir: intervention.disponibilite_a_partir,
      depart_pour_mission: intervention.depart_pour_mission,
      engin_1: intervention.engin_1,
      engin_2: intervention.engin_2,
      engin_3: intervention.engin_3,
      engin_4: intervention.engin_4,
    };

    setEditingIntervention(duplicatedIntervention as Intervention);
    setShowAddModal(true);
    showNotification('Formulaire pré-rempli avec les données de l\'intervention', 'info');
  }, [showNotification]);

  const handleSaveIntervention = useCallback(async (interventionData: Partial<Intervention>) => {
    try {

      if (editingIntervention?.id) {
        await updateIntervention(editingIntervention.id, interventionData);
        showNotification('Intervention modifiée', 'success');
      } else {
        await createIntervention(interventionData);
        showNotification('Intervention créée', 'success');
      }
      setShowAddModal(false);
      setEditingIntervention(undefined);

      await loadInterventions(activeFilter || undefined);

      window.dispatchEvent(new CustomEvent('pendingModificationsUpdated'));
    } catch (error: any) {
      showNotification(error.message || 'Erreur lors de l\'enregistrement', 'error');
    }
  }, [editingIntervention, updateIntervention, createIntervention, showNotification, loadInterventions, activeFilter]);

  const handleSendEmail = async (data: EmailData) => {
    try {
      onShowLoading(true);
      const response = await emailApi.sendEmail(data);
      setEmailData(response);
      setShowEmailModal(false);
      setShowEmailPreview(true);
    } catch (error: any) {
      showNotification(error.message || 'Erreur lors de la préparation de l\'email', 'error');
    } finally {
      onShowLoading(false);
    }
  };

  const handleSendEmailViaAutomate = async (recipient: string, subject: string) => {
    if (!emailData) {
      console.error('SendEmailViaAutomate: emailData est null');
      showNotification('Erreur: Données d\'email manquantes', 'error');
      return;
    }

    if (!emailData.htmlContent) {
      console.error('SendEmailViaAutomate: htmlContent est manquant dans emailData', emailData);
      showNotification('Erreur: Contenu HTML manquant', 'error');
      return;
    }

    try {
      onShowLoading(true);
      console.log('SendEmailViaAutomate: Début d\'envoi', { recipient, subject });

      const webhookUrl = 'https://default4a7c823857994b169fc69ad8fce5a7.d9.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/a51b6e3b094d4fd19377cd1b64e45f85/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=ZKGVMxBOqNVec0EnMcP-jKfNdsz4MCrM_2I0Xmb7VfA';

      const payload = {
        to: recipient,
        from: 'mn_gof_stf-infra@sncf.fr',
        subject: subject,
        htmlBody: emailData.htmlContent,
        cc: ['MN_GOF_STF-INFRA@sncf.fr'],
        timestamp: new Date().toISOString()
      };

      console.log('SendEmailViaAutomate: Envoi de la requête au webhook Power Automate');

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('SendEmailViaAutomate: Réponse du webhook', {
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('SendEmailViaAutomate: Erreur du webhook', errorText);
        showNotification(`Erreur d'envoi: ${response.status} - ${errorText}`, 'error');
        return;
      }

      setShowEmailPreview(false);
      showNotification('Email envoyé avec succès!', 'success');
      console.log('SendEmailViaAutomate: Email envoyé avec succès');

      try {
        await emailApi.logPowerAutomate({
          recipient: recipient,
          subject: subject,
          campaign: emailData.interventions?.[0]?.campagne_nom || '',
          intervention_count: emailData.interventions?.length || 0,
          intervention_ids: emailData.interventions?.map((i: any) => i.id) || []
        });
        console.log('SendEmailViaAutomate: Log Power Automate enregistré');
      } catch (logError: any) {
        console.error('SendEmailViaAutomate: Erreur lors de l\'enregistrement du log', logError);

      }

    } catch (error: any) {
      console.error('SendEmailViaAutomate: Erreur fatale', error);
      showNotification('Erreur lors de l\'envoi: ' + (error.message || 'Erreur inconnue'), 'error');
    } finally {
      onShowLoading(false);
    }
  };

  const handleRequestModification = async (id: number, modifiedData: Partial<Intervention>, reason: string) => {
    try {
      const response = await fetch('/api/pending-modifications/request-modification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          intervention_id: id,
          donnees_modifiees: modifiedData,
          motif: reason
        })
      });

      if (response.ok) {
        showNotification('Modification soumise pour validation GOF', 'success');

        await loadInterventions(activeFilter || undefined);
        await fetchPendingCount();

        window.dispatchEvent(new CustomEvent('interventionsUpdated'));
        window.dispatchEvent(new CustomEvent('pendingModificationsUpdated'));
      } else {
        const errorData = await response.json();
        showNotification(errorData.error || 'Erreur lors de la demande de modification', 'error');
        return;
      }
    } catch (error: any) {
      showNotification(error instanceof Error ? error.message : 'Erreur lors de la demande de modification', 'error');
    }
  };

  const handleRequestModificationClick = (id: number) => {
    const intervention = interventions.find(i => i.id === id);
    setPendingAction({ type: 'modify', intervention });
    setEditingIntervention(intervention);
    setShowModifyModal(true);
  };

  const handleModificationSave = async (modifiedData: Partial<Intervention>) => {
    if (!pendingAction?.intervention) return;

    const originalData = pendingAction.intervention;
    const hasRealChanges = Object.keys(modifiedData).some(key => {
      const originalValue = originalData[key as keyof Intervention];
      const modifiedValue = modifiedData[key as keyof Intervention];

      if (key.includes('date') || key === 'disponibilite_a_partir' || key === 'depart_pour_mission') {
        try {
          const origTime = originalValue ? new Date(originalValue as string).getTime() : null;
          const modTime = modifiedValue ? new Date(modifiedValue as string).getTime() : null;
          return origTime !== modTime;
        } catch {
          return originalValue !== modifiedValue;
        }
      }

      return originalValue !== modifiedValue;
    });

    if (!hasRealChanges) {
      showNotification('Aucune modification détectée', 'info');
      setShowModifyModal(false);
      setEditingIntervention(undefined);
      setPendingAction(null);
      return;
    }

    try {
      await handleRequestModification(pendingAction.intervention.id, modifiedData, 'Demande de modification via formulaire');

      await fetchPendingCount();
    } finally {
      setShowModifyModal(false);
      setEditingIntervention(undefined);
      setPendingAction(null);
    }
  };

  return (
    <>
      <Sidebar
        onShowValidationLogs={() => {
          setShowValidationLogs(true);
          if (!user?.isGOF && user) {
            localStorage.setItem(`history_last_seen_${user.id}`, Date.now().toString());
            setHistoryNotifications(0);
          }
        }}
        onShowGOF={() => setShowGOF(true)}
        onShowStats={() => setShowStats(true)}
        onShowFilters={() => setShowFilters(prev => !prev)}
        onNewIntervention={() => {
          setEditingIntervention(undefined);
          setShowAddModal(true);
        }}
        onSendEmail={() => setShowEmailModal(true)}
        onShowLineManagement={() => setShowLineManagement(true)}
        pendingCount={user?.isGOF ? 0 : historyNotifications}
        lineManagementCount={user?.isGOF ? pendingCount : 0}
      />

      <div className="app-main-content">
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '0.5rem' }}>
          <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
            <div className="card-header" style={{ padding: '0.5rem 1rem', borderBottom: '1px solid var(--border-light)', display: 'flex', alignItems: 'center' }}>
              <div className="flex items-center justify-between" style={{ width: '100%', alignItems: 'center' }}>
                <div className="flex items-center flex-wrap gap-2" style={{ alignItems: 'center' }}>
                  <h2 className="text-base mb-0 font-semibold flex items-center">
                    <i className="fas fa-list mr-2 text-primary"></i>
                    Liste des Interventions
                  </h2>
                  <button
                    onClick={() => {
                      setActiveFilter('');
                      void loadInterventions();
                    }}
                    className={`badge cursor-pointer transition-all ${activeFilter === '' ? 'badge-primary' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    style={{ fontSize: '0.7rem' }}
                    title="Cliquez pour voir toutes les interventions"
                  >
                    <i className="fas fa-th-list mr-1"></i>
                    Toutes les interventions :
                    <span className="ml-1.5 font-bold">{allInterventions.length}</span>
                  </button>

                  {/* Badges Stats cliquables - Couleurs alignées avec surlignage */}
                  <button
                    onClick={() => handleFilterByStatus('realized')}
                    className={`stats-badge ${activeFilter === 'realized' ? 'active' : ''}`}
                    style={{
                      background: activeFilter === 'realized' ? 'linear-gradient(180deg, #34C759 0%, #28A745 100%)' : 'rgba(52, 199, 89, 0.15)',
                      color: activeFilter === 'realized' ? 'white' : '#14532d',
                      boxShadow: activeFilter === 'realized' ? '0 2px 8px rgba(52, 199, 89, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : '0 1px 3px rgba(52, 199, 89, 0.2)'
                    }}
                  >
                    <i className="fas fa-check-circle mr-1"></i>
                    <span>Intervention Réalisée :</span>
                    <span className="ml-1.5 font-bold">{stats.totalRealized}</span>
                  </button>

                  <button
                    onClick={() => handleFilterByStatus('refused')}
                    className={`stats-badge ${activeFilter === 'refused' ? 'active' : ''}`}
                    style={{
                      background: activeFilter === 'refused' ? 'linear-gradient(180deg, #eab308 0%, #ca8a04 100%)' : 'rgba(234, 179, 8, 0.15)',
                      color: activeFilter === 'refused' ? 'white' : '#854d0e',
                      boxShadow: activeFilter === 'refused' ? '0 2px 8px rgba(234, 179, 8, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : '0 1px 3px rgba(234, 179, 8, 0.2)'
                    }}
                  >
                    <i className="fas fa-times-circle mr-1"></i>
                    <span>Intervention Refusée par le SR :</span>
                    <span className="ml-1.5 font-bold">{stats.totalRefused}</span>
                  </button>

                  <button
                    onClick={() => handleFilterByStatus('cancelled')}
                    className={`stats-badge ${activeFilter === 'cancelled' ? 'active' : ''}`}
                    style={{
                      background: activeFilter === 'cancelled' ? 'linear-gradient(180deg, #FF9500 0%, #D77D00 100%)' : 'rgba(255, 149, 0, 0.15)',
                      color: activeFilter === 'cancelled' ? 'white' : '#9a3412',
                      boxShadow: activeFilter === 'cancelled' ? '0 2px 8px rgba(255, 149, 0, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : '0 1px 3px rgba(255, 149, 0, 0.2)'
                    }}
                  >
                    <i className="fas fa-ban mr-1"></i>
                    <span>Intervention Annulée par le Client :</span>
                    <span className="ml-1.5 font-bold">{stats.totalCancelled}</span>
                  </button>

                  <button
                    onClick={() => handleFilterByStatus('validated-next-week')}
                    className={`stats-badge ${activeFilter === 'validated-next-week' ? 'active' : ''}`}
                    style={{
                      background: activeFilter === 'validated-next-week' ? 'linear-gradient(180deg, #5AC8FA 0%, #30B0C7 100%)' : 'rgba(90, 200, 250, 0.15)',
                      color: activeFilter === 'validated-next-week' ? 'white' : '#0c4a6e',
                      boxShadow: activeFilter === 'validated-next-week' ? '0 2px 8px rgba(90, 200, 250, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : '0 1px 3px rgba(90, 200, 250, 0.2)'
                    }}
                  >
                    <i className="fas fa-calendar-plus mr-1"></i>
                    <span>Intervention de la S+1 :</span>
                    <span className="ml-1.5 font-bold">{stats.validatedNextWeek}</span>
                  </button>

                  <button
                    onClick={() => handleFilterByStatus('validated-week')}
                    className={`stats-badge ${activeFilter === 'validated-week' ? 'active' : ''}`}
                    style={{
                      background: activeFilter === 'validated-week' ? 'linear-gradient(180deg, #FF2D55 0%, #D70015 100%)' : 'rgba(255, 45, 85, 0.15)',
                      color: activeFilter === 'validated-week' ? 'white' : '#9f1239',
                      boxShadow: activeFilter === 'validated-week' ? '0 2px 8px rgba(255, 45, 85, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : '0 1px 3px rgba(255, 45, 85, 0.2)'
                    }}
                  >
                    <i className="fas fa-calendar-week mr-1"></i>
                    <span>Intervention de la semaine :</span>
                    <span className="ml-1.5 font-bold">{stats.validatedThisWeek}</span>
                  </button>

                  <button
                    onClick={() => handleFilterByStatus('pending')}
                    className={`stats-badge ${activeFilter === 'pending' ? 'active' : ''}`}
                    style={{
                      background: activeFilter === 'pending' ? 'linear-gradient(180deg, #007AFF 0%, #0051D5 100%)' : 'rgba(0, 122, 255, 0.15)',
                      color: activeFilter === 'pending' ? 'white' : '#1e40af',
                      boxShadow: activeFilter === 'pending' ? '0 2px 8px rgba(0, 122, 255, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)' : '0 1px 3px rgba(0, 122, 255, 0.2)'
                    }}
                  >
                    <i className="fas fa-clock mr-1"></i>
                    <span>Intervention en attente :</span>
                    <span className="ml-1.5 font-bold">{stats.totalPending}</span>
                  </button>
                </div>
              </div>
            </div>

            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <div className="table-container" style={{ height: '100%', width: '100%' }}>
                <InterventionTable
                  interventions={interventions}
                  isGOF={user?.isGOF || false}
                  canModify={user?.canModify || false}
                  userRole={user?.role}
                  onEdit={handleEditIntervention}
                  onDelete={handleDeleteIntervention}
                  onRequestModification={handleRequestModificationClick}
                  onDuplicate={handleDuplicateIntervention}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <InterventionModal
        show={showAddModal}
        intervention={editingIntervention}
        campaigns={campaigns}
        sites={sites}
        technicentres={technicentres}
        prestations={prestations}
        isGOF={user?.isGOF || false}
        onSave={handleSaveIntervention}
        onClose={() => {
          setShowAddModal(false);
          setEditingIntervention(undefined);
        }}
      />

      <EmailModal
        show={showEmailModal}
        campaigns={campaigns}
        technicentres={technicentres}
        onSend={handleSendEmail}
        onClose={() => setShowEmailModal(false)}
      />

      <EmailPreviewModal
        show={showEmailPreview}
        emailData={emailData}
        onSend={handleSendEmailViaAutomate}
        onClose={() => setShowEmailPreview(false)}
      />

      <LineManagementModal
        show={showLineManagement}
        onClose={() => setShowLineManagement(false)}
        onRefresh={() => loadInterventions()}
      />

      {/* Modal de validation pour les technicentres - Étape 1: Sélection des engins */}
      {user?.role === 'technicentre' && editingIntervention && (
        <TechnicentreValidationModal
          show={showModifyModal}
          intervention={editingIntervention}
          onSave={handleSaveIntervention}
          onClose={() => {
            setShowModifyModal(false);
            setEditingIntervention(undefined);
          }}
          onContinue={(selected, rejected) => {

            setSelectedEngines(selected);
            setRejectedEngines(rejected);
            setShowModifyModal(false);
            setShowFinalValidationModal(true);
          }}
        />
      )}

      {/* Modal de validation finale pour les technicentres - Étape 2: Date SR + Commentaires */}
      {user?.role === 'technicentre' && editingIntervention && (
        <TechnicentreFinalValidationModal
          show={showFinalValidationModal}
          intervention={editingIntervention}
          selectedEngines={selectedEngines}
          rejectedEngines={rejectedEngines}
          onSave={() => {
            setShowFinalValidationModal(false);
            setEditingIntervention(undefined);
            setSelectedEngines([]);
            setRejectedEngines([]);
          }}
          onClose={() => {
            setShowFinalValidationModal(false);
            setEditingIntervention(undefined);
            setSelectedEngines([]);
            setRejectedEngines([]);
          }}
        />
      )}

      {/* Modal de modification standard pour les autres utilisateurs */}
      {user?.role !== 'technicentre' && (
        <InterventionModal
          show={showModifyModal}
          intervention={editingIntervention}
          campaigns={campaigns}
          sites={sites}
          technicentres={technicentres}
          prestations={prestations}
          isGOF={false}
          onSave={handleModificationSave}
          onClose={() => {
            setShowModifyModal(false);
            setEditingIntervention(undefined);
            setPendingAction(null);
          }}
        />
      )}

      <StatsModal
        show={showStats}
        interventions={interventions}
        campaigns={campaigns}
        onClose={() => setShowStats(false)}
      />

      <GOFPanel
        show={showGOF}
        onClose={() => setShowGOF(false)}
        isGOF={user?.isGOF || false}
      />

      <HistoriqueGlobalModal
        show={showValidationLogs}
        onClose={() => setShowValidationLogs(false)}
      />

      <FilterPanel
        show={showFilters}
        onToggle={() => setShowFilters(prev => !prev)}
        campaigns={campaigns}
        technicentres={technicentres}
        prestations={prestations}
        filters={filters}
        onFilterChange={setFilters}
        onClearFilters={handleClearFilters}
        isGOF={user?.isGOF || false}
      />
    </>
  );
};

export default Dashboard;
